import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Components
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MainLayout from './components/Layout/MainLayout';

// Raw Material Pages
import SteelReceiptReport from './pages/RawMaterial/SteelReceiptReport';
import MillTestCertificate from './pages/RawMaterial/MillTestCertificate';
import ProductionSteelIssue from './pages/RawMaterial/ProductionSteelIssue';
import MechanicalProperties from './pages/RawMaterial/MechanicalProperties';
import RMStatusUpdate from './pages/RawMaterial/RMStatusUpdate';
import KFLChemistry from './pages/RawMaterial/KFLChemistry';
import ViewSRR from './pages/RawMaterial/ViewSRR';
import ViewMTC from './pages/RawMaterial/ViewMTC';

// Heat Treatment Pages
import HTProduction from './pages/HeatTreatment/HTProduction';
import DailyProduction from './pages/HeatTreatment/DailyProduction';
import HTProductionReports from './pages/HeatTreatment/HTProductionReports';

// Reports Pages
import ProductionGradeReport from './pages/Reports/ProductionGradeReport';
import RMStatusReport from './pages/Reports/RMStatusReport';
import ReworkAnalysis from './pages/Reports/ReworkAnalysis';
import SteelFormatReport from './pages/Reports/SteelFormatReport';
import TestCertificateReport from './pages/Reports/TestCertificateReport';
import QFBRReport from './pages/Reports/QFBRReport';
import MicrostructureReport from './pages/Reports/MicrostructureReport';
import PeriodicSummary from './pages/Reports/PeriodicSummary';
import PeriodicReports from './pages/Reports/PeriodicReports';
import RawMaterialTestingReport from './pages/Reports/RawMaterialTestingReport';

// Raw Material Pages (additional)
import RawMaterialPlan from './pages/RawMaterial/RawMaterialPlan';

// Master Pages
import DieMaster from './pages/Masters/DieMaster';
import CustomerMaster from './pages/Masters/CustomerMaster';
import SteelMaster from './pages/Masters/SteelMaster';
import SupplierMaster from './pages/Masters/SupplierMaster';
import HTCodeMaster from './pages/Masters/HTCodeMaster';
import AgentMaster from './pages/Masters/AgentMaster';
import SpecMaster from './pages/Masters/SpecMaster';

// Placeholder components for pages not yet implemented
const PlaceholderPage = ({ title, description }) => (
  <div style={{ padding: '20px', textAlign: 'center' }}>
    <h2>{title}</h2>
    <p>{description}</p>
    <p style={{ color: '#666', fontStyle: 'italic' }}>
      This screen will be implemented as part of the complete FoxPro migration.
    </p>
  </div>
);

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1976d2',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#fafafa',
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? children : <Navigate to="/login" />;
};

// Public Route component (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider 
          maxSnack={3} 
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <AuthProvider>
            <Router>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } />
                
                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Raw Material Routes */}
                <Route path="/raw-material/srr" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SteelReceiptReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/mtc" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <MillTestCertificate />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/mechanical" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <MechanicalProperties />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/psis" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ProductionSteelIssue />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/update" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RMStatusUpdate />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/chemistry" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <KFLChemistry />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/view-srr" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ViewSRR />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/raw-material/view-mtc" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ViewMTC />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Heat Treatment Routes */}
                <Route path="/heat-treatment/production" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <HTProduction />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/heat-treatment/daily" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DailyProduction />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/heat-treatment/reports" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <HTProductionReports />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Master Data Routes */}
                <Route path="/masters/dies" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DieMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/steels" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SteelMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/suppliers" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/customers" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <CustomerMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/ht-codes" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <HTCodeMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/agents" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <AgentMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/masters/specifications" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SpecMaster />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Raw Material View/Print Routes */}
                <Route path="/raw-material/status" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RMStatusReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Heat Treatment View/Print Routes */}
                <Route path="/heat-treatment/rework" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ReworkAnalysis />
                    </MainLayout>
                  </ProtectedRoute>
                } />

                {/* Reports Routes */}
                <Route path="/reports/steel-format" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SteelFormatReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/rm-plan" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RawMaterialPlan />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/production-grade" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ProductionGradeReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/test-certificate" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <TestCertificateReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/qfbr" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <QFBRReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/microstructure" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <MicrostructureReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/periodic" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PeriodicSummary />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/periodic-reports" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <PeriodicReports />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                <Route path="/reports/rm-testing" element={
                  <ProtectedRoute>
                    <MainLayout>
                      <RawMaterialTestingReport />
                    </MainLayout>
                  </ProtectedRoute>
                } />
                
                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Router>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;